import { useCallback, useEffect, useState } from 'react';
import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import { Modal, Tabs, Form, useForm, toast } from '../../../ui';
import accountService from '../../../services/accountService';
import {
  TextField,
  PasswordField,
  PASSWORD_HINT,
  newPasswordRules,
  matchesField,
  normalizeGithubInput,
  GITHUB_USERNAME_PATTERN,
} from '../../auth/components/FormFields';
import '../style.css';

const githubRules = [
  {
    validator: (_, value) => {
      const username = normalizeGithubInput(value);
      return !username || GITHUB_USERNAME_PATTERN.test(username)
        ? Promise.resolve()
        : Promise.reject(new Error("That isn't a valid GitHub username. Use letters, numbers and single hyphens."));
    },
  },
];

/**
 * Edit name, email and GitHub username, and change the password.
 * Changing the GitHub username moves access to purchased repositories to the new account
 * and can then not be changed again for 30 days (Modules/Accounts/AccountService.cs).
 *
 * onSaved(user) runs after the details are saved. initialTab: 'details' | 'password'.
 */
const AccountSettingsModal = ({ open, onClose, onSaved, initialTab = 'details' }) => {
  const detailsForm = useForm();
  const passwordForm = useForm();
  const [tab, setTab] = useState(initialTab);
  const [profile, setProfile] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  // Set while the customer confirms a GitHub username change: { from, to, payload }
  const [githubChange, setGithubChange] = useState(null);

  const load = useCallback(async () => {
    setLoadState('loading');
    setLoadError('');
    const result = await accountService.getMe();
    if (!result.success || !result.data) {
      setLoadError(result.error || 'Could not load your details.');
      setLoadState('error');
      return;
    }
    setProfile(result.data);
    detailsForm.resetFields();
    detailsForm.setFieldsValue({
      fullName: result.data.fullName || '',
      email: result.data.email || '',
      gitHubUsername: result.data.gitHubUsername || '',
    });
    setLoadState('ready');
  }, [detailsForm]);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setFormError('');
    setGithubChange(null);
    passwordForm.resetFields();
    load();
  }, [open, initialTab, load, passwordForm]);

  const changeTab = key => {
    setTab(key);
    setFormError('');
  };

  const submitDetails = async payload => {
    setSaving(true);
    setFormError('');
    const result = await accountService.updateMe(payload);
    setSaving(false);
    if (!result.success) {
      setGithubChange(null);
      setFormError(result.error);
      return;
    }
    setGithubChange(null);
    if (result.data) setProfile(result.data);
    toast.success({ content: result.message || 'Your details were saved.', duration: 6 });
    onSaved?.(result.data);
    onClose?.();
  };

  const saveDetails = values => {
    if (saving) return;
    const gitHubUsername = normalizeGithubInput(values.gitHubUsername);
    const payload = {
      fullName: values.fullName.trim(),
      email: values.email?.trim() || undefined,
      gitHubUsername: gitHubUsername || undefined,
    };
    const previous = profile?.gitHubUsername || '';
    if (previous && gitHubUsername && gitHubUsername.toLowerCase() !== previous.toLowerCase()) {
      setGithubChange({ from: previous, to: gitHubUsername, payload });
      return;
    }
    submitDetails(payload);
  };

  const changePassword = async values => {
    if (saving) return;
    setSaving(true);
    setFormError('');
    const result = await accountService.changePassword(values);
    setSaving(false);
    if (!result.success) {
      setFormError(result.error);
      return;
    }
    passwordForm.resetFields();
    toast.success(result.message || 'Password changed.');
    onClose?.();
  };

  const errorAlert = formError && (
    <div className="pk-alert" data-tone="danger" role="alert">
      <CircleAlert aria-hidden="true" />
      <span>{formError}</span>
    </div>
  );

  const missingEmail = profile && !profile.email;
  const missingGitHub = profile && !profile.gitHubUsername;

  const renderDetails = () => {
    if (loadState === 'loading') {
      return (
        <div className="pk-settings__loading" aria-busy="true" aria-label="Loading your details">
          <span className="pk-skel" />
          <span className="pk-skel" />
          <span className="pk-skel" />
        </div>
      );
    }
    if (loadState === 'error') {
      return (
        <div className="pk-alert" data-tone="danger" role="alert">
          <CircleAlert aria-hidden="true" />
          <div className="pk-settings__alert-body">
            <span>{loadError}</span>
            <button type="button" className="pk-btn pk-btn--secondary pk-btn--xs" onClick={load}>
              Retry
            </button>
          </div>
        </div>
      );
    }
    if (githubChange) {
      return (
        <div className="pk-settings__confirm" role="group" aria-labelledby="github-change-title">
          <h3 className="pk-title-3" id="github-change-title">
            Change your GitHub username?
          </h3>
          <div className="pk-alert" data-tone="warning">
            <TriangleAlert aria-hidden="true" />
            <div className="pk-settings__alert-body">
              <span>
                Access to every repository you've bought moves from <strong>@{githubChange.from}</strong> to{' '}
                <strong>@{githubChange.to}</strong>. @{githubChange.from} loses access, and GitHub sends @
                {githubChange.to} an invitation to accept for each repository.
              </span>
              <span>After this change you can't change your GitHub username again for 30 days.</span>
            </div>
          </div>
          {errorAlert}
          <div className="pk-settings__actions">
            <button
              type="button"
              className="pk-btn pk-btn--primary pk-btn--sm"
              aria-busy={saving || undefined}
              onClick={() => submitDetails(githubChange.payload)}
            >
              Change to @{githubChange.to}
            </button>
            <button
              type="button"
              className="pk-btn pk-btn--secondary pk-btn--sm"
              onClick={() => {
                setGithubChange(null);
                setFormError('');
              }}
              disabled={saving}
            >
              Go back
            </button>
          </div>
        </div>
      );
    }
    return (
      <Form form={detailsForm} onFinish={saveDetails} className="pk-form">
        {(missingEmail || missingGitHub) && (
          <div className="pk-alert" data-tone="info">
            <Info aria-hidden="true" />
            <div className="pk-settings__alert-body">
              {missingEmail && <span>Add an email to receive receipts, delivery emails and password reset codes.</span>}
              {missingGitHub && <span>Add your GitHub username to receive access to purchased repositories.</span>}
            </div>
          </div>
        )}
        <TextField
          name="fullName"
          label="Full name"
          autoComplete="name"
          rules={[
            { required: true, whitespace: true, message: 'Enter your name.' },
            { min: 2, max: 50, message: 'Use 2 to 50 characters.' },
          ]}
        />
        <TextField
          name="email"
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          rules={[{ type: 'email', message: 'Enter a valid email address.' }]}
        />
        <TextField
          name="gitHubUsername"
          label="GitHub username"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="octocat"
          hint="Repository access for your purchases goes to this account. It can be changed once every 30 days."
          rules={githubRules}
        />
        {profile?.phone && (
          <p className="pk-hint">
            You sign in with {profile.phone}. To change your phone number, contact support.
          </p>
        )}
        {errorAlert}
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--block" aria-busy={saving || undefined}>
          Save changes
        </button>
      </Form>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Account settings" footer={null} width={520}>
      <div className="pk-settings">
        <Tabs
          activeKey={tab}
          onChange={changeTab}
          items={[
            { key: 'details', label: 'Details', children: renderDetails() },
            {
              key: 'password',
              label: 'Password',
              children: (
                <Form form={passwordForm} onFinish={changePassword} className="pk-form">
                  <PasswordField
                    name="currentPassword"
                    label="Current password"
                    autoComplete="current-password"
                    rules={[{ required: true, message: 'Enter your current password.' }]}
                  />
                  <PasswordField
                    name="newPassword"
                    label="New password"
                    autoComplete="new-password"
                    hint={PASSWORD_HINT}
                    rules={newPasswordRules}
                  />
                  <PasswordField
                    name="confirmNewPassword"
                    label="Confirm new password"
                    autoComplete="new-password"
                    rules={[
                      { required: true, message: 'Enter the new password again.' },
                      matchesField('newPassword'),
                    ]}
                  />
                  {errorAlert}
                  <button
                    type="submit"
                    className="pk-btn pk-btn--primary pk-btn--block"
                    aria-busy={saving || undefined}
                  >
                    Change password
                  </button>
                </Form>
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default AccountSettingsModal;
